import { useMemo } from 'react';
import type { Lens } from '@hookform/lenses';
import { useWatch } from 'react-hook-form';
import { z } from 'zod/v4';
import { Grid } from '@interxfi/design-system/components/alignment/grid';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { useDateAfterTodayDeterminer } from '@interxfi/design-system/components/input/date-picker-input/util';
import { useTableOfContentsContext } from '@interxfi/design-system/components/table-of-contents/table-of-contents-context';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ResponsiveValue } from '@interxfi/design-system/util/responsive';
import { type I18nNamespaces, useTranslation } from '@interxfi/translation';
import {
  IsoV1Country,
  IxcRegistrationV1BusinessClassification,
  IxcRegistrationV1EntityType,
  type IxcRegistrationV1OrganizationProfile,
  IxcRegistrationV1UsState,
} from '@interxfi/types/generated/api';
import { AppDatePickerInputWithLens } from '../../../../../components/form/app-date-picker-input';
import { AppEnumSelectInputWithLens } from '../../../../../components/form/app-enum-select-input';
import { AppSelectInputWithLens } from '../../../../../components/form/app-select-input';
import { AppTextAreaInputWithLens } from '../../../../../components/form/app-text-area-input';
import { AppTextInputWithLens } from '../../../../../components/form/app-text-input';
import { getOptionalSchema, OptionalField, type OptionalFieldWrapper } from '../../../../../components/form/optional-field';
import { buildCountryOptions, buildStateOptions } from '../../../../../util/country';
import { addDefinedValues } from '../../../../../util/has-value';
import {
  AddressForm,
  type AddressFormLabels,
  type AddressFormValues,
  addressFormSchema,
  defaultAddressFormValues,
  formValuesToAddress,
  getInitialFormValuesForAddress,
} from '../../../shared-form/address/address-form';
import { defaultPhoneValues, formValuesToPhones, getInitialFormValuesForPhones, PhonesForm, phoneFormSchema } from '../../../shared-form/phone-form';
import {
  controlPersonFormSchema,
  formValuesToControlPerson,
  formValuesToPrimaryContactProfileId,
  getInitialFormValuesForControlPerson,
} from './control-person/control-person-form';
import { ControlPersonsTableForm, validateControlPersons } from './control-person/control-persons-table-form';
import {
  formValuesToOrganizationW9,
  getInitialFormValuesForOrganizationW9,
  OrganizationProfileW9Form,
  organizationProfileW9FormDefaultValues,
  organizationProfileW9FormSchema,
} from './organization-profile-w9-form';
import {
  defaultOrganizationTaxIdValues,
  formValuesToOrganizationTaxIdentifier,
  getInitialFormValuesForOrganizationTaxId,
  OrganizationTaxIdForm,
  organizationTaxIdFormSchema,
} from './organization-tax-identifier-form';

const countryOfFormationOptions = buildCountryOptions('en-US', Object.values(IsoV1Country));

export const organizationProfileFormSchema = z.object({
  type: z.literal('organization'),
  value: z
    .object({
      legalName: z.string().nonempty({ error: 'Legal Name is required' }),
      dbaName: z.string().or(z.literal('')),
      email: z.email({ error: 'A valid email address is required' }).nonempty({ error: 'Email is required' }),
      taxIdentifier: organizationTaxIdFormSchema,
      companyAddress: addressFormSchema,
      mailingAddress: getOptionalSchema(addressFormSchema),
      businessClassification: z.enum(IxcRegistrationV1BusinessClassification),
      dateOfFormation: z.iso.date().or(z.literal('')),
      countryOfFormation: z.enum(IsoV1Country),
      stateOfFormation: z.enum(IxcRegistrationV1UsState).or(z.literal('')),
      phone: z.array(phoneFormSchema),
      entityType: z.enum(IxcRegistrationV1EntityType).or(z.literal('')),
      controlPersons: z.array(controlPersonFormSchema),
      natureOfBusiness: z.string().nonempty({ error: 'Nature of Business is required' }),
      w9: organizationProfileW9FormSchema,
    })
    .superRefine((data, ctx) => {
      if (
        [
          IxcRegistrationV1EntityType.LlcCCorporation,
          IxcRegistrationV1EntityType.LlcSCorporation,
          IxcRegistrationV1EntityType.LlcPartnership,
        ].includes(data.entityType as IxcRegistrationV1EntityType)
      ) {
        if (data.taxIdentifier.type === 'ssn') {
          ctx.addIssue({
            code: 'custom',
            message: 'The entity type selected must be established with an EIN.',
            path: ['taxIdentifier', 'type'],
          });
        }
      }
    }),
});

export const onSubmitOrganizationProfileFormSchema = organizationProfileFormSchema.safeExtend({
  value: organizationProfileFormSchema.shape.value.safeExtend({
    controlPersons: organizationProfileFormSchema.shape.value.shape.controlPersons.refine(validateControlPersons, {
      error: 'At least one officer or director is required',
    }),
  }),
});

export type OrganizationProfileFormValues = z.infer<typeof organizationProfileFormSchema>;

export const defaultOrganizationProfileFormValues: OrganizationProfileFormValues = {
  type: 'organization',
  value: {
    legalName: '',
    dbaName: '',
    email: '',
    taxIdentifier: defaultOrganizationTaxIdValues,
    countryOfFormation: IsoV1Country.Us,
    stateOfFormation: '',
    dateOfFormation: '',
    businessClassification: '' as IxcRegistrationV1BusinessClassification, // Trick to satisfy z.enum requirement
    companyAddress: { ...defaultAddressFormValues },
    mailingAddress: {
      isProviding: false,
      value: undefined,
    },
    phone: [{ ...defaultPhoneValues }],
    entityType: '',
    controlPersons: [],
    natureOfBusiness: '',
    w9: organizationProfileW9FormDefaultValues,
  },
};

export function getInitialFormValuesForOrganizationProfile(profile: IxcRegistrationV1OrganizationProfile | undefined): OrganizationProfileFormValues {
  return {
    ...defaultOrganizationProfileFormValues,
    value: addDefinedValues<OrganizationProfileFormValues['value']>(
      {
        ...defaultOrganizationProfileFormValues.value,
        mailingAddress: {
          isProviding: Boolean(profile?.mailingAddress),
          value: profile?.mailingAddress ? getInitialFormValuesForAddress(profile?.mailingAddress) : undefined,
        },
      },
      {
        legalName: profile?.legalName,
        dbaName: profile?.dbaName,
        email: profile?.email,
        businessClassification: profile?.businessClassification,
        natureOfBusiness: profile?.natureOfBusiness,
        taxIdentifier: profile?.taxIdentifier
          ? getInitialFormValuesForOrganizationTaxId(profile.taxIdentifier)
          : defaultOrganizationProfileFormValues.value.taxIdentifier,
        companyAddress: getInitialFormValuesForAddress(profile?.companyAddress),
        phone: getInitialFormValuesForPhones(profile?.phone),
        entityType: profile?.entityType,
        countryOfFormation: profile?.countryOfFormation || undefined,
        stateOfFormation: profile?.stateOfFormation || undefined,
        dateOfFormation: profile?.dateOfFormation,
        controlPersons: profile?.controlPersons?.map((owner) => getInitialFormValuesForControlPerson(owner, profile?.primaryContactProfileId)),
        w9: profile?.w9 ? getInitialFormValuesForOrganizationW9(profile.w9) : undefined,
      },
    ),
  };
}

export function formValuesToOrganizationProfile(values: OrganizationProfileFormValues): IxcRegistrationV1OrganizationProfile {
  return addDefinedValues<IxcRegistrationV1OrganizationProfile>(
    {
      legalName: values.value.legalName,
      email: values.value.email,
      businessClassification: values.value.businessClassification,
      companyAddress: formValuesToAddress(values.value.companyAddress),
      countryOfFormation: values.value.countryOfFormation,
      taxIdentifier: formValuesToOrganizationTaxIdentifier(values.value.taxIdentifier),
      natureOfBusiness: values.value.natureOfBusiness,
      w9: formValuesToOrganizationW9(values.value.w9),
    },
    {
      dbaName: values.value.dbaName,
      entityType: values.value.entityType || undefined,
      mailingAddress:
        values.value.mailingAddress.isProviding && values.value.mailingAddress.value
          ? formValuesToAddress(values.value.mailingAddress.value)
          : undefined,
      phone: formValuesToPhones(values.value.phone),
      controlPersons: values.value.controlPersons.map(formValuesToControlPerson),
      primaryContactProfileId: formValuesToPrimaryContactProfileId(values.value.controlPersons),
      stateOfFormation: values.value.stateOfFormation || undefined,
      dateOfFormation: values.value.dateOfFormation,
    },
  );
}

function sortBusinessClassificationOptions(a: IxcRegistrationV1BusinessClassification, b: IxcRegistrationV1BusinessClassification) {
  if (a === IxcRegistrationV1BusinessClassification.None) {
    return -1;
  }

  if (b === IxcRegistrationV1BusinessClassification.None) {
    return 1;
  }

  return a.localeCompare(b);
}

const mailingAddressLabels = {
  usAddress: {
    street1: 'Street Address or PO Box',
  },
} satisfies AddressFormLabels;

export enum OrganizationProfileTableOfContentsItem {
  GeneralInformation = 'generalInformation',
  W9 = 'w9',
  ControlPersons = 'controlPersons',
}

const generalInfoColumns: ResponsiveValue<number> = {
  sm: 1,
  md: 2,
};

interface OrganizationProfileFormProps {
  lens: Lens<OrganizationProfileFormValues>;
  programId: string | undefined;
}

const i18nNamespaces: I18nNamespaces[] = ['ixcRegistrationV1'] as const;

export function OrganizationProfileForm({ lens, programId }: OrganizationProfileFormProps) {
  const { t } = useTranslation(i18nNamespaces);
  const { getTabFocusInSectionProps } = useTableOfContentsContext<OrganizationProfileTableOfContentsItem>();
  const stateOptions = useMemo(() => buildStateOptions(t, IsoV1Country.Us), [t]);
  const countryOfFormation = useWatch({ ...lens.focus('value.countryOfFormation').interop(), defaultValue: IsoV1Country.Us });
  const companyFormationDateDeterminer = useDateAfterTodayDeterminer();

  return (
    <VerticalStack gap={7}>
      <VerticalStack gap={7} {...getTabFocusInSectionProps(OrganizationProfileTableOfContentsItem.GeneralInformation)}>
        <VerticalStack gap={4}>
          <Grid autoColumns={generalInfoColumns} gap={4}>
            <AppTextInputWithLens label="Legal Name" lens={lens.focus('value.legalName')} required />
            <AppTextInputWithLens label="DBA" lens={lens.focus('value.dbaName')} />

            <OrganizationTaxIdForm lens={lens.focus('value.taxIdentifier')} withoutContainer />

            <AppEnumSelectInputWithLens
              enumName="IxcRegistrationV1EntityType"
              enumValue={IxcRegistrationV1EntityType}
              i18nNamespace="ixcRegistrationV1"
              label="Entity Type"
              lens={lens.focus('value.entityType')}
            />

            <AppEnumSelectInputWithLens
              enumName="IxcRegistrationV1BusinessClassification"
              enumValue={IxcRegistrationV1BusinessClassification}
              i18nNamespace="ixcRegistrationV1"
              info="These high risk business classifications require disclosure."
              label="Business Classification"
              lens={lens.focus('value.businessClassification')}
              required
              sortByLabel={sortBusinessClassificationOptions}
            />

            <AppSelectInputWithLens
              label="Country of Formation"
              lens={lens.focus('value.countryOfFormation')}
              options={countryOfFormationOptions}
              required
            />

            <AppDatePickerInputWithLens
              dateAvailabilityDeterminer={companyFormationDateDeterminer}
              label="Date of Formation"
              lens={lens.focus('value.dateOfFormation')}
              required
            />
          </Grid>

          {countryOfFormation === IsoV1Country.Us ? (
            <AppSelectInputWithLens label="State of Formation" lens={lens.focus('value.stateOfFormation')} options={stateOptions} required />
          ) : null}

          <AppTextAreaInputWithLens
            hint="Please describe the type of activities the organization is involved in and how revenue is generated."
            label="Nature of Business"
            lens={lens.focus('value.natureOfBusiness')}
            required
            rows={3}
          />

          <AppTextInputWithLens autoComplete="off" label="Email Address" lens={lens.focus('value.email')} required />
        </VerticalStack>

        <VerticalStack gap={4}>
          <Typography.Level3 as="h2">Phone</Typography.Level3>

          <PhonesForm labelPlaceholder="Headquarters, Office..." lens={lens.focus('value.phone')} minLength={1} />
        </VerticalStack>

        <VerticalStack gap={4}>
          <Typography.Level3 as="h2">Company Address</Typography.Level3>

          <AddressForm lens={lens.focus('value.companyAddress')} />
        </VerticalStack>

        <VerticalStack gap={7}>
          <VerticalStack gap={4}>
            <Typography.Level3 as="h2">Mailing Address</Typography.Level3>

            <OptionalField<AddressFormValues>
              defaultValue={defaultAddressFormValues}
              isInverted
              label="Same as company address"
              lens={lens.focus('value.mailingAddress').narrow<OptionalFieldWrapper<AddressFormValues>>()}
              variant="standalone"
            >
              <AddressForm labels={mailingAddressLabels} lens={lens.focus('value.mailingAddress.value').defined()} />
            </OptionalField>
          </VerticalStack>
        </VerticalStack>
      </VerticalStack>

      <VerticalStack gap={7} {...getTabFocusInSectionProps(OrganizationProfileTableOfContentsItem.W9)}>
        <VerticalStack gap={4}>
          <Typography.Level3 as="h2">W-9</Typography.Level3>

          <OrganizationProfileW9Form lens={lens.focus('value.w9')} />
        </VerticalStack>
      </VerticalStack>

      <VerticalStack gap={7} {...getTabFocusInSectionProps(OrganizationProfileTableOfContentsItem.ControlPersons)}>
        <VerticalStack gap={4}>
          <Typography.Level3 as="h2">Control Persons</Typography.Level3>

          <ControlPersonsTableForm lens={lens.focus('value.controlPersons')} programId={programId} />
        </VerticalStack>
      </VerticalStack>
    </VerticalStack>
  );
}
